import React, { useEffect, useState } from 'react';
import Spinner from '../components/ui/Spinner';
import Pagination from '../components/Pagination';
import ClientCard from '../components/mobile/Client';
import ClientHeader from '../components/common/Client';
import ClientTable from '../components/desktop/Client';
import ClientForm from '../components/form/Client';
import { createClient, getClients, toggleClientStatus, updateClientPartial } from '../services/clients';
import { getCustomers } from '../services/customers';
import { getBranches } from '../services/branches';
import ConfirmationModal from '../components/modals/ConfirmationModal';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [count, setCount] = useState(0);
  const [errors, setErrors] = useState({});
  const [editingClient, setEditingClient] = useState(null);
  const [initialData, setInitialData] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [inputSearch, setInputSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [branchFilter, setBranchFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmModalParams, setConfirmModalParams] = useState({
    open: false,
    title: 'Are you sure?',
    message: 'Do you really want to proceed with this action?',
    confirmText: 'Yes, Do it',
    cancelText: 'No, Cancel',
  });
  const clientsPerPage = 20;

  useEffect(() => {
    setPendingPage(1);
  }, [search, statusFilter, branchFilter, customerFilter]);

  useEffect(() => {
    if (pendingPage != null) {
      fetchClients(pendingPage);
    }
  }, [pendingPage]);

  useEffect(() => {
    fetchCustomers();
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    const [err, data] = await getBranches({ paginate: false });
    if (!err) setBranches(Array.isArray(data) ? data : (data?.results || []));
  };

  const fetchCustomers = async () => {
    await getCustomers({ paginate: false, status: 'active' })
      .then(([err, data]) => {
        if (!err) {
          setCustomers(Array.isArray(data) ? data : (data?.results || []));
        }
      })
      .catch(() => {});
  };

  const fetchClients = async (page) => {
    setLoading(true);
    const params = {
      page, search,
      status: statusFilter.toLowerCase(),
      pageSize: clientsPerPage,
      branch: branchFilter || null,
      customer: customerFilter || null,
    };
    await getClients(params)
      .then(([err, data]) => {
        const rows = Array.isArray(data?.results) ? data.results : [];
        const normalizedFilter = (statusFilter || 'all').toLowerCase();
        const filteredRows = normalizedFilter === 'active'
          ? rows.filter((item) => Boolean(item?.is_active))
          : normalizedFilter === 'inactive'
          ? rows.filter((item) => !Boolean(item?.is_active))
          : rows;
        setClients(filteredRows);
        setCount(data.count);
        setCurrentPage(page);
        setPendingPage(null);
        setLoading(false);
      })
      .catch((error) => {
        setPendingPage(null);
        setLoading(false);
      });
  };

  const handleAdd = () => {
    setEditingClient(null);
    setShowForm(true);
    setErrors({});
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setInitialData(client);
    setShowForm(true);
    setErrors({});
  };

  const getChangedFields = (initial, updated) => {
    const changed = {};
    for (const key in updated) {
      if (Array.isArray(updated[key])) {
        if (JSON.stringify(initial[key]) !== JSON.stringify(updated[key])) {
          changed[key] = updated[key];
        }
      } else if (updated[key] !== initial[key]) {
        changed[key] = updated[key];
      }
    }
    return changed;
  };

  const handleSave = async (clientData) => {
    let err, response;
    setLoading(true);
    if (editingClient) {
      const changedFields = getChangedFields(initialData, clientData);
      if (Object.keys(changedFields).length === 0) {
        return;
      }
      [err, response] = await updateClientPartial(clientData.id, changedFields);
      if (!err) {
        setInitialData({});
      }
    } else {
      clientData.is_active = 1;
      [err, response] = await createClient(clientData);
    }
    setLoading(false);
    if (err) {
      setErrors(err);
    } else {
      await fetchClients(currentPage);
      setShowForm(false);
    }
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearch(inputSearch.trim());
  };

  const onSearchTextChange = (e) => {
    setInputSearch(e.target.value);
  };

  const onClearSearch = () => {
    setInputSearch('');
    setSearch('');
    setCurrentPage(1);
    setPendingPage(1);
  };

  const onStatusFilter = (e) => setStatusFilter(e.target.value);
  const onBranchFilter = (e) => setBranchFilter(e.target.value);
  const onCustomerFilter = (e) => setCustomerFilter(e.target.value);

  const handleToggleStatus = async (client) => {
    setConfirmModalParams({
      ...confirmModalParams,
      open: true,
      title: client.is_active ? 'Deactivate Client' : 'Activate Client',
      message: `Are you sure you want to ${client.is_active ? 'deactivate' : 'activate'} the client "${client.name}"?`,
      onConfirm: () => handleAction(client),
    });
  };

  const handleAction = async (client) => {
    setLoading(true);
    await toggleClientStatus(client.id, !client.is_active);
    setConfirmModalParams({ ...confirmModalParams, open: false });
    await fetchClients(currentPage);
    setLoading(false);
  };

  const handleClientSelection = (client) => {
    setEditingClient(client);
    setShowForm(true);
  };

  return (
    <div className="p-4 w-full">
      <ClientHeader
        inputSearch={inputSearch}
        count={count}
        onSearchSubmit={onSearchSubmit}
        onSearchTextChange={onSearchTextChange}
        handleAdd={handleAdd}
        onClearSearch={onClearSearch}
        statusFilter={statusFilter}
        onStatusFilter={onStatusFilter}
        branchFilter={branchFilter}
        onBranchFilter={onBranchFilter}
        customerFilter={customerFilter}
        onCustomerFilter={onCustomerFilter}
        branches={branches}
        customers={customers}
        client={editingClient}
        showForm={showForm}
      />
      

      {showForm ? (
        <ClientForm
          client={editingClient}
          customers={customers}
          dataErrors={errors}
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
        />
      ) : (
        <>
          {loading ? <Spinner /> : (
            <>
              <ClientCard
                clients={clients}
                handleEdit={handleEdit}
                handleToggleStatus={handleToggleStatus}
              />

              <ClientTable
                clients={clients}
                handleEdit={handleEdit}
                handleToggleStatus={handleToggleStatus}
                onClientSelected={handleClientSelection}
              />
            </>
          )}

          {/* ✅ Pagination */}
          <div className="md:static fixed bottom-0 left-0 w-full bg-white border-t shadow z-10">
            {clients.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.max(1, Math.ceil(count / clientsPerPage))}
                onPageChange={setPendingPage}
              />
            )}
          </div>
        </>
      )}
      <ConfirmationModal
        open={confirmModalParams.open}
        title={confirmModalParams.title}
        message={confirmModalParams.message}
        onConfirm={confirmModalParams.onConfirm}
        onCancel={() => setConfirmModalParams({...confirmModalParams, open: false})}
        confirmText={confirmModalParams.confirmText}
        cancelText={confirmModalParams.cancelText}
      />
    </div>
  );
};

export default Clients;
